import { motion } from "framer-motion";
import { Phone, Heart, Users, Wrench, Shield, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";

const PHONE = "5599996208";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

const VALUES = [
  {
    icon: Heart,
    title: "Dedication",
    desc: "Every pool is treated like it's our own. We take pride in delivering exceptional results every single visit.",
  },
  {
    icon: Users,
    title: "Community",
    desc: "We're not just a service provider — we're your neighbors. We've built lasting relationships with families across Fresno & Clovis.",
  },
  {
    icon: Wrench,
    title: "Craftsmanship",
    desc: "With over 6 years of hands-on experience, Pete brings deep expertise and attention to detail to every job.",
  },
  {
    icon: Shield,
    title: "Trust",
    desc: "Our A+ BBB Rating reflects our commitment to honest, reliable service. Your satisfaction is our reputation.",
  },
];

export default function AboutPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="overflow-hidden">
      {/* ═══ HERO ═══ */}
      <section className="relative py-32 md:py-40 bg-pool-deep overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/wk257s3w_Screenshot%202026-03-05%20at%207.23.48%E2%80%AFPM.png"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-pool-deep/80" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              Our Story
            </p>
            <h1 className="font-heading text-5xl md:text-7xl font-bold text-white tracking-tight mb-6">
              Meet Pete Cervantes
            </h1>
            <p className="text-white/60 text-lg md:text-xl max-w-2xl mx-auto">
              The trusted pool professional behind Fresno & Clovis's most
              dependable pool service.
            </p>
          </motion.div>
        </div>
        {/* Bottom wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-10">
          <svg viewBox="0 0 1440 80" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0,40 C360,80 720,0 1080,40 C1260,60 1380,50 1440,40 L1440,80 L0,80 Z" fill="white" />
          </svg>
        </div>
      </section>

      {/* ═══ STORY ═══ */}
      <section data-testid="about-story" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <motion.div {...fadeUp} className="lg:col-span-5">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden shadow-2xl shadow-pool-deep/15">
                  <img
                    src="https://customer-assets.emergentagent.com/job_petes-pool-fresno/artifacts/kbm9gdiv_Screenshot%202026-04-09%20at%208.44.57%E2%80%AFPM.png"
                    alt="Pete Cervantes - Owner of Pete's Pool Service & Repair"
                    className="w-full aspect-[4/5] object-cover object-top max-h-[480px]"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-6 -right-6 glass-panel rounded-2xl p-4 shadow-xl">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-gold/10 flex items-center justify-center">
                      <Shield size={22} className="text-gold" />
                    </div>
                    <div>
                      <p className="font-bold text-pool-deep text-sm">A+ BBB</p>
                      <p className="text-xs text-slate-500">Since 2019</p>
                    </div>
                  </div>
                </div>
                <div className="absolute -top-4 -left-4 glass-panel rounded-xl p-3 shadow-lg">
                  <p className="text-pool-aqua font-bold text-sm">15+ Yrs</p>
                  <p className="text-[10px] text-slate-500">Experience</p>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div {...fadeUp} className="lg:col-span-7">
              <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
                About Pete
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-pool-deep tracking-tight mb-8">
                Built on Hard Work
                <br />
                <span className="text-pool-aqua">& Trust</span>
              </h2>
              <div className="space-y-5 text-slate-600 text-base md:text-lg leading-relaxed">
                <p>
                  When Pete Cervantes founded Pete's Pool Service & Repair in March
                  2019, he had a simple mission: provide Fresno and Clovis
                  homeowners with pool service they could truly count on.
                </p>
                <p>
                  With over 15 years of experience in the pool industry — and six
                  of those running his own business — Pete brings deep expertise
                  and a personal touch to every job. As an owner-operated business,
                  Pete personally oversees every visit, ensuring the same level of
                  care and attention to detail that earned him an A+ rating from
                  the Better Business Bureau.
                </p>
                <p>
                  Whether it's a routine maintenance visit or a challenging
                  pool restoration, Pete approaches every job with
                  pride in his workmanship and a genuine commitment to his
                  customers' satisfaction.
                </p>
                <p>
                  Fluent in both English and Spanish, Pete serves a diverse
                  community of homeowners who trust him with their backyard
                  investments. His friendly, professional approach has made him one
                  of the most trusted independent pool professionals in the
                  Central Valley.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ VALUES ═══ */}
      <section data-testid="about-values" className="py-24 md:py-32 bg-slate-50 water-shimmer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="text-center mb-16">
            <p className="text-pool-aqua text-sm font-bold tracking-[0.2em] uppercase mb-4">
              What Drives Us
            </p>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-pool-deep tracking-tight">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {VALUES.map((val, i) => {
              const Icon = val.icon;
              return (
                <motion.div
                  key={val.title}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="flex gap-6 p-8 bg-white rounded-2xl border border-slate-100 hover:shadow-xl hover:shadow-pool-aqua/5 transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 rounded-2xl bg-pool-aqua/10 flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-pool-aqua" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-bold text-pool-deep mb-2">
                      {val.title}
                    </h3>
                    <p className="text-slate-500 text-sm leading-relaxed">
                      {val.desc}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section className="py-20 md:py-28 bg-pool-deep">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6 tracking-tight">
              Ready to Experience the Difference?
            </h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
              Join the families in Fresno & Clovis who trust Pete with their pool.
              Call today for a free consultation.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href={`tel:${PHONE}`}
                data-testid="about-call-now"
                className="inline-flex items-center gap-3 bg-pool-aqua text-pool-deep px-10 py-5 rounded-full text-lg font-bold transition-all duration-300 hover:shadow-xl hover:shadow-pool-aqua/30 hover:-translate-y-1"
              >
                <Phone size={22} />
                Call Pete Now
              </a>
              <Link
                to="/contact"
                data-testid="about-contact-link"
                className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white px-10 py-5 rounded-full text-lg font-medium transition-all duration-300 hover:bg-white/20 hover:-translate-y-1"
              >
                Contact Us <ArrowRight size={18} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
